import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './Policy.css';

const PrivacyPolicy = () => {
  return (
    <div className="App">
      <Header />
      <div className="page policy-page">
        <div className="container">
          <div className="policy-header">
            <h1>Privacy Policy</h1>
            <p className="policy-updated">Last Updated: January 2025 (Updated for HDFC Bank Smart Gateway Integration)</p>
          </div>

          <div className="policy-content">
            <section className="policy-section">
              <h2>1. Introduction</h2>
              <div className="policy-text">
                <p>
                  SLN BOOK WORLD ("we," "our," or "us") is committed to protecting your privacy. 
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your 
                  information when you use our website and services.
                </p>
                <p>
                  By using our website, you consent to the collection and use of information in 
                  accordance with this policy.
                </p>
              </div>
            </section>

            <section className="policy-section">
              <h2>2. Information We Collect</h2>
              <div className="policy-text">
                <h3>2.1 Personal Information</h3>
                <p>We collect information that you provide directly to us, including:</p>
                <ul>
                  <li>Name, email address, phone number</li>
                  <li>Delivery address and billing information</li>
                  <li>School information and student details</li>
                  <li>Payment information (processed securely through HDFC Bank Smart Gateway)</li>
                  <li>Account credentials and preferences</li>
                </ul>

                <h3>2.2 Automatically Collected Information</h3>
                <p>When you visit our website, we automatically collect:</p>
                <ul>
                  <li>IP address and browser type</li>
                  <li>Device information and operating system</li>
                  <li>Pages visited and time spent on pages</li>
                  <li>Referring website addresses</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>

                <h3>2.3 Third-Party Information</h3>
                <p>
                  We may receive information about you from third parties, such as HDFC Bank (our payment 
                  gateway provider) and delivery partners, to facilitate order processing and delivery. 
                  Payment transactions are processed securely through HDFC Bank Smart Gateway, which 
                  complies with PCI-DSS security standards.
                </p>
              </div>
            </section>

            <section className="policy-section">
              <h2>3. How We Use Your Information</h2>
              <div className="policy-text">
                <p>We use the collected information for the following purposes:</p>
                <ul>
                  <li>Process and fulfill your orders</li>
                  <li>Manage your account and provide customer support</li>
                  <li>Send order confirmations, updates, and delivery notifications</li>
                  <li>Process payments and prevent fraud</li>
                  <li>Improve our website, products, and services</li>
                  <li>Send promotional communications (with your consent)</li>
                  <li>Comply with legal obligations</li>
                  <li>Respond to your inquiries and requests</li>
                </ul>
              </div>
            </section>

            <section className="policy-section">
              <h2>4. Information Sharing and Disclosure</h2>
              <div className="policy-text">
                <h3>4.1 Service Providers</h3>
                <p>
                  We may share your information with trusted third-party service providers who assist 
                  us in operating our website, processing payments, delivering orders, and providing 
                  customer support. These providers are contractually obligated to protect your information.
                </p>

                <h3>4.2 Legal Requirements</h3>
                <p>
                  We may disclose your information if required by law or in response to valid requests 
                  by public authorities, court orders, or legal processes.
                </p>

                <h3>4.3 Business Transfers</h3>
                <p>
                  In the event of a merger, acquisition, or sale of assets, your information may be 
                  transferred to the acquiring entity.
                </p>

                <h3>4.4 We Do Not Sell Your Data</h3>
                <p>
                  We do not sell, rent, or trade your personal information to third parties for 
                  their marketing purposes.
                </p>
              </div>
            </section>

            <section className="policy-section">
              <h2>5. Data Security</h2>
              <div className="policy-text">
                <p>
                  We implement appropriate technical and organizational security measures to protect 
                  your personal information against unauthorized access, alteration, disclosure, or 
                  destruction. These measures include:
                </p>
                <ul>
                  <li>SSL encryption for data transmission</li>
                  <li>Secure payment processing through HDFC Bank Smart Gateway (PCI-DSS compliant)</li>
                  <li>3D Secure authentication for card payments</li>
                  <li>Tokenization of sensitive payment data</li>
                  <li>Regular security assessments and updates</li>
                  <li>Access controls and authentication measures</li>
                  <li>Secure data storage and backup systems</li>
                </ul>
                <p>
                  However, no method of transmission over the Internet or electronic storage is 
                  100% secure. While we strive to protect your information, we cannot guarantee 
                  absolute security.
                </p>
              </div>
            </section>

            <section className="policy-section">
              <h2>6. Cookies and Tracking Technologies</h2>
              <div className="policy-text">
                <p>
                  We use cookies and similar tracking technologies to enhance your browsing experience, 
                  analyze website traffic, and personalize content. You can control cookie preferences 
                  through your browser settings.
                </p>
                <p>
                  Types of cookies we use:
                </p>
                <ul>
                  <li><strong>Essential Cookies:</strong> Required for website functionality</li>
                  <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our website</li>
                  <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                </ul>
              </div>
            </section>

            <section className="policy-section">
              <h2>7. Your Rights and Choices</h2>
              <div className="policy-text">
                <p>You have the following rights regarding your personal information:</p>
                <ul>
                  <li><strong>Access:</strong> Request access to your personal data</li>
                  <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal data</li>
                  <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                  <li><strong>Data Portability:</strong> Request a copy of your data in a portable format</li>
                  <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing</li>
                </ul>
                <p>
                  To exercise these rights, please contact us at <a href="mailto:support@slnbooks.com">support@slnbooks.com</a>
                </p>
              </div>
            </section>

            <section className="policy-section">
              <h2>8. Data Retention</h2>
              <div className="policy-text">
                <p>
                  We retain your personal information for as long as necessary to fulfill the purposes 
                  outlined in this policy, unless a longer retention period is required or permitted by law. 
                  Account information is retained while your account is active and for a reasonable period 
                  thereafter for legal and business purposes.
                </p>
              </div>
            </section>

            <section className="policy-section">
              <h2>9. Children's Privacy</h2>
              <div className="policy-text">
                <p>
                  Our services are intended for use by parents and guardians purchasing books for children. 
                  We do not knowingly collect personal information directly from children under 18 years of age. 
                  If you believe we have collected information from a child, please contact us immediately.
                </p>
              </div>
            </section>

            <section className="policy-section">
              <h2>10. Changes to This Privacy Policy</h2>
              <div className="policy-text">
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of any changes 
                  by posting the new policy on this page and updating the "Last Updated" date. 
                  Your continued use of our services after changes are posted constitutes acceptance 
                  of the updated policy.
                </p>
              </div>
            </section>

            <section className="policy-section">
              <h2>11. Contact Us</h2>
              <div className="policy-text">
                <p>
                  If you have questions or concerns about this Privacy Policy or our data practices, 
                  please contact us:
                </p>
                <ul>
                  <li><strong>Email:</strong> <a href="mailto:support@slnbooks.com">support@slnbooks.com</a></li>
                  <li><strong>Phone:</strong> <a href="tel:+919916832476">+91-9916832476</a>, <a href="tel:+919663622125">+91-9663622125</a></li>
                  <li><strong>Address:</strong> Ground Floor, 4 & 5, 6th Main Road, Dattatreya Nagar, Banashankari Stage 3, Bengaluru – 560085</li>
                </ul>
              </div>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;

